import { IonIcon } from '@ionic/react';
import { addOutline, checkmarkOutline } from 'ionicons/icons';
import React, { useState } from 'react';
import type { WorkflowRunState } from '../../automation/runProgress';
import {
  buildPlainEnglishSegments,
  parseExtensionList,
  type PlainEnglishSegment,
} from '../../automation/workflowSummary';
import {
  getLinearActionNodes,
  getNodeValidationIssues,
  START_NODE_ID,
} from '../../automation/workflowGraph';
import type {
  AutomationExecutionEntry,
  AutomationGraph,
  AutomationTrigger,
} from '../../types/AutomationWorkflow.types';
import { getAutomationBlockIcon } from './automationBlockIcons';
import { WorkflowActionCard } from './WorkflowActionCard';
import { WorkflowIfElseCard } from './WorkflowIfElseCard';
import { WorkflowTriggerCard } from './WorkflowTriggerCard';
import type { StepRunStatus } from './WorkflowStepCardParts';

export const WorkflowPlainEnglishSummary: React.FC<{
  trigger: AutomationTrigger;
  graph: AutomationGraph;
}> = ({ trigger, graph }) => {
  const segments = buildPlainEnglishSegments(trigger, graph);
  return (
    <section className="automation-plain-english">
      <p className="automation-plain-english-label">In plain english</p>
      <p className="automation-plain-english-text">
        {segments.map((segment, index) => (
          <PlainEnglishSpan
            key={`${segment.text}-${index}`}
            segment={segment}
          />
        ))}
      </p>
    </section>
  );
};

const PlainEnglishSpan: React.FC<{ segment: PlainEnglishSegment }> = ({
  segment,
}) =>
  segment.highlight ? (
    <span className="automation-plain-highlight">{segment.text}</span>
  ) : (
    <>{segment.text}</>
  );

type MarkerState = 'idle' | 'running' | 'done' | 'error';

export const WorkflowStepList: React.FC<{
  graph: AutomationGraph;
  trigger: AutomationTrigger;
  executionLog: AutomationExecutionEntry[];
  selectedNodeId: string | null;
  runState: WorkflowRunState;
  onTriggerChange: (trigger: AutomationTrigger) => void;
  onSelectNode: (nodeId: string) => void;
  onNodeConfigChange: (nodeId: string, config: Record<string, unknown>) => void;
  onRemove: (nodeId: string) => void;
  onReorder: (draggedNodeId: string, targetNodeId: string) => void;
  onPickFolder: () => void | Promise<void>;
  onAddStep: () => void;
}> = ({
  graph,
  trigger,
  executionLog,
  selectedNodeId,
  runState,
  onTriggerChange,
  onSelectNode,
  onNodeConfigChange,
  onRemove,
  onReorder,
  onPickFolder,
  onAddStep,
}) => {
  const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null);
  const actions = getLinearActionNodes(graph);
  const extensions = parseExtensionList(trigger.config.extensions);
  const triggerTouched =
    runState.running || Object.keys(runState.completed).length > 0;

  const dragHandlersFor = (nodeId: string) => ({
    onDragStart: (event: React.DragEvent) => {
      event.dataTransfer.effectAllowed = 'move';
      setDraggedNodeId(nodeId);
    },
    onDragOver: (event: React.DragEvent) => event.preventDefault(),
    onDrop: (event: React.DragEvent) => {
      event.preventDefault();
      if (draggedNodeId && draggedNodeId !== nodeId) {
        onReorder(draggedNodeId, nodeId);
      }
      setDraggedNodeId(null);
    },
    onDragEnd: () => setDraggedNodeId(null),
  });

  const statusFor = (nodeId: string): StepRunStatus => ({
    running: runState.activeNodeId === nodeId,
    durationMs: runState.completed[nodeId],
  });

  const markerStateFor = (nodeId: string): MarkerState => {
    if (runState.failedNodeId === nodeId) return 'error';
    if (runState.activeNodeId === nodeId) return 'running';
    if (runState.completed[nodeId] != null) return 'done';
    return 'idle';
  };

  return (
    <div className="automation-timeline">
      <WorkflowTimelineStep
        marker="trigger"
        markerState={triggerTouched ? 'done' : 'idle'}
        lineActive={triggerTouched}
      >
        <WorkflowTriggerCard
          trigger={trigger}
          extensions={extensions}
          issues={getNodeValidationIssues(graph, trigger, START_NODE_ID)}
          onTriggerChange={onTriggerChange}
          onPickFolder={() => void onPickFolder()}
        />
      </WorkflowTimelineStep>

      {actions.map((node, index) => {
        const markerState = markerStateFor(node.id);
        const shared = {
          selected: selectedNodeId === node.id,
          issues: getNodeValidationIssues(graph, trigger, node.id),
          status: statusFor(node.id),
          drag: dragHandlersFor(node.id),
          onSelect: () =>
            onSelectNode(selectedNodeId === node.id ? '' : node.id),
          onRemove: () => onRemove(node.id),
          onConfigChange: (config: Record<string, unknown>) =>
            onNodeConfigChange(node.id, config),
        };

        return (
          <WorkflowTimelineStep
            key={node.id}
            marker={String(index + 1)}
            markerState={markerState}
            lineActive={markerState === 'done' || markerState === 'running'}
          >
            {node.type === 'if_else' ? (
              <WorkflowIfElseCard
                node={node}
                executionLog={executionLog}
                {...shared}
              />
            ) : (
              <WorkflowActionCard node={node} {...shared} />
            )}
          </WorkflowTimelineStep>
        );
      })}

      <div className="automation-timeline-step automation-add-step-row">
        <div className="automation-timeline-rail">
          <span className="automation-timeline-line automation-add-step-stub" />
        </div>
        <button
          type="button"
          className="automation-add-step-btn"
          onClick={onAddStep}
        >
          <IonIcon icon={addOutline} aria-hidden />
          <span>Add a step</span>
          <span className="automation-add-step-hint">or press ⌘K</span>
        </button>
      </div>

      <div className="automation-timeline-step automation-end-row last">
        <div className="automation-timeline-rail">
          <span className="automation-timeline-marker end">
            <IonIcon icon={checkmarkOutline} />
          </span>
        </div>
        <p className="automation-end-label">Workflow finishes</p>
      </div>
    </div>
  );
};

const WorkflowTimelineStep: React.FC<{
  marker: string;
  markerState: MarkerState;
  lineActive: boolean;
  children: React.ReactNode;
}> = ({ marker, markerState, lineActive, children }) => {
  const isTrigger = marker === 'trigger';

  return (
    <div className="automation-timeline-step">
      <div className="automation-timeline-rail">
        <span
          className={`automation-timeline-line${isTrigger ? ' trigger-line' : ''}${lineActive ? ' active' : ''}`}
          aria-hidden
        />
        <span
          className={`automation-timeline-marker${isTrigger ? ' trigger' : ''} ${markerState}`}
        >
          {isTrigger ? (
            <IonIcon icon={getAutomationBlockIcon('trigger')} />
          ) : markerState === 'done' ? (
            <IonIcon icon={checkmarkOutline} />
          ) : (
            marker
          )}
        </span>
      </div>
      <div className="automation-timeline-content">{children}</div>
    </div>
  );
};
